import ESigningFormPersonalNumber from '../ESigningForm.personalNumber';
import ESigningFormPremiumAgreement from './ESigningFormPremiumAgreement';

/**
 * Step-1 logic for the combined ClimateAndNaturePlus form - fills in and signs a
 * ClimateAndNature agreement and a NaturePlus agreement together in one session.
 *
 * Orchestrates two non-standalone ESigningFormPremiumAgreement instances (one per section) and
 * owns the shared Step1Next button, delivery period field, and submit payload/endpoint.
 */
export default class ESigningFormCombinedPremiumAgreement {
    constructor(base) {
        this.base = base;
        this.base.submitEndpoint = '/api/e-signing/v2/premium/initiate-combined-signing';
        this.base.buildPayload = this.buildPayload;

        this.deliveryPeriodInput = document.getElementById('premiumDeliveryPeriod');

        this.climateNature = new ESigningFormPremiumAgreement(base, 'ClimateNature', {
            isStandalone: false,
            managesDeliveryPeriodList: true,
            itemsFormType: 'ClimateAndNature',
            onStateChange: () => this.updateStep1NextButtonState(),
        });
        this.naturePlus = new ESigningFormPremiumAgreement(base, 'NaturePlus', {
            isStandalone: false,
            managesDeliveryPeriodList: false,
            itemsFormType: 'NaturePlus',
            onStateChange: () => this.updateStep1NextButtonState(),
        });

        this.initStep1EventListeners();
        this.updateStep1NextButtonState();
    }

    initStep1EventListeners() {
        const step1NextBtn = document.querySelector('#Step1Next');
        if (step1NextBtn) {
            step1NextBtn.addEventListener('click', this.handleStep1Next);
        }

        if (this.deliveryPeriodInput) {
            ['input', 'change', 'blur'].forEach((eventName) => {
                this.deliveryPeriodInput.addEventListener(eventName, () => this.updateStep1NextButtonState());
            });
        }
    }

    isStep1Valid() {
        const hasDeliveryPeriod = (this.deliveryPeriodInput?.value || '').trim() !== '';
        return hasDeliveryPeriod
            && this.climateNature.step1Validation.isStep1Valid()
            && this.naturePlus.step1Validation.isStep1Valid();
    }

    validateStep1() {
        // Run both so errors are shown for whichever section(s) are incomplete, instead of
        // short-circuiting on the first invalid section.
        const climateNatureValid = this.climateNature.step1Validation.validateStep1();
        const naturePlusValid = this.naturePlus.step1Validation.validateStep1();
        const hasDeliveryPeriod = (this.deliveryPeriodInput?.value || '').trim() !== '';
        return hasDeliveryPeriod && climateNatureValid && naturePlusValid;
    }

    handleStep1Next = () => {
        if (!this.validateStep1()) {
            return;
        }

        this.base.showStep(2);
    };

    updateStep1NextButtonState() {
        const nextButton = document.getElementById('Step1Next');
        if (!nextButton) {
            return;
        }

        const hasTemplate = (document.getElementById('signingTemplate')?.value || '').trim() !== '';
        this.base.toggleStep1NextVisibility(hasTemplate);

        if (this.isStep1Valid()) {
            nextButton.classList.remove('disabled');
        } else {
            nextButton.classList.add('disabled');
        }
    }

    buildVariantPayload(suffix, formType) {
        const getVal = (id) => document.getElementById(id)?.value || '';
        const itemInput = document.getElementById('premiumProductItem' + suffix);
        const priceRaw = itemInput?.dataset?.price || '';
        const price = priceRaw === '' ? null : Number.parseFloat(priceRaw);
        const quantity = getVal('contractQuantity' + suffix) || getVal('premiumHectare' + suffix);
        const climateNatureMeasuresRaw = getVal('climateNatureMeasures' + suffix).trim();
        const climateNatureMeasures = climateNatureMeasuresRaw ? JSON.parse(climateNatureMeasuresRaw) : [];

        return {
            formType,
            premiumProductItem: getVal('premiumProductItem' + suffix),
            premiumProductName: itemInput?.dataset?.name || '',
            price: Number.isNaN(price) ? null : price,
            premiumHectare: quantity,
            contractQuantity: quantity,
            climateNatureMeasures,
        };
    }

    buildPayload = async () => {
        const getVal = (id) => document.getElementById(id)?.value || '';
        const currentSigner = {
            name: getVal('CurrentSignerName'),
            email: getVal('CurrentSignerEmail'),
            mobileNumber: getVal('CurrentSignerPhoneNumber'),
            personalNumber: ESigningFormPersonalNumber.normalizePersonalNumber(getVal('CurrentSignerPersonalNumber')),
        };
        const selectedSigner = {
            name: getVal('ReceiverName'),
            email: getVal('ReceiverEmail'),
            mobileNumber: getVal('ReceiverPhoneNumber'),
            personalNumber: ESigningFormPersonalNumber.getReceiverPersonalNumberValue(this.base),
        };
        const includeSellerSigner = document.getElementById('step2View')?.dataset.includeSellerSigner === 'true';

        return {
            templateId: getVal('signingTemplate'),
            customerNumber: getVal('customerNumberElementId'),
            duration: parseInt(getVal('signingDuration')) || 30,
            deadlineTime: getVal('deadlineTime') || null,
            pageId: parseInt(getVal('pageContentId')) || null,
            templateSettingsBlockId: parseInt(getVal('templateSettingsBlockId')) || null,
            signers: includeSellerSigner ? [currentSigner, selectedSigner] : [selectedSigner],
            premiumDeliveryPeriod: getVal('premiumDeliveryPeriod'),
            agreements: [
                this.buildVariantPayload('ClimateNature', 'ClimateAndNature'),
                this.buildVariantPayload('NaturePlus', 'NaturePlus'),
            ],
        };
    };
}
